import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { CustomEase } from '../gsap/CustomEase.min.js'

import { CONTENT_Y_DISTANCE, constructAnimationVars } from './gsap-shared'

gsap.registerPlugin(ScrollTrigger, CustomEase)

CustomEase.create('easeOutCubic', 'M0,0 C0.33,1 0.68,1 1,1')

// Presets
gsap.defaults({
    duration: 0.7,
    ease: 'easeOutCubic',
})

const contentAnimation = {
    breakpoint: 640,
    shareLinksWrap: $('.js-gsap-share-wrap'),
    shareLinks: $('.js-gsap-share'),
    single: gsap.utils.toArray('.js-gsap-single'),
    singleHeaders: gsap.utils.toArray('.js-gsap-single-header'), //Added different animation rules for specific titles below headers
    delayedWrap: gsap.utils.toArray('.js-gsap-wrap'),
    delayedWrapFromDesktop: gsap.utils.toArray('.js-gsap-wrap-dsk'),
    delayedWrapFromTablet: gsap.utils.toArray('.js-gsap-wrap-tbl'),
    delayedWrapOnMobile: gsap.utils.toArray('.js-gsap-wrap-mob'),
    captions: gsap.utils.toArray('figcaption'),
    rteWrap: $('.js-gsap-rte-wrap'),
    init: function () {
        ScrollTrigger.saveStyles(this.shareLinks)

        // always single animation
        this.single.forEach(element => this.initSingleAnimation(element))

        // kill and reinit single animations on orientationchange - safari related bug
        window.addEventListener('orientationchange', e => {
            let singleAnim = ScrollTrigger.getById('singleAnimation')
            singleAnim.kill()

            setTimeout(() => {
                this.single.forEach(element =>
                    this.initSingleAnimation(element)
                )
            }, 400)
        }),
            //initiate animation  only for titles below heading
            this.singleHeaders.forEach(element =>
                this.initHeadingAnimation(element)
            )

        // always delayed animation
        this.delayedWrap.forEach(wrapper => {
            const elements = wrapper.querySelectorAll('.js-gsap-delay')
            if (elements.length) this.initDelayedAnimation(wrapper, elements)
        })

        // all direct children of article RTE content wrapper
        const $rteChildren = this.rteWrap.children()
        const $rteChildrenArray = gsap.utils.toArray($rteChildren)
        $rteChildrenArray.forEach(element =>
            this.initSingleAnimation(element, element)
        )

        // photo captions inside  article RTE content wrapper
        this.captions.forEach(caption => {
            const captionTimeline = gsap.timeline({
                paused: true,
            })
            captionTimeline
                .set(caption, {
                    color: 'transparent',
                })
                .to(caption, {
                    width: '100%',
                    duration: 0.3,
                    ease: 'easeOutCubic',
                })
                .to(caption, {
                    height: 'auto',
                    duration: 0.3,
                    ease: 'easeOutCubic',
                })
                .set(caption, {
                    color: 'rgba(11, 22, 58, 0.8)',
                })

            $(caption).on('click', ({ target }) => {
                let $target = $(target)
                if (!$target.hasClass('.is-revealed')) {
                    captionTimeline.play()
                    $target.addClass('.is-revealed')
                } else {
                    captionTimeline.reverse()
                    $target.removeClass('.is-revealed')
                }
            })
        })

        ScrollTrigger.matchMedia({
            '(min-width: 640px)': () => {
                // alt 1: single on mobile, delayed from tablet up
                this.delayedWrapFromTablet.forEach(wrapper => {
                    const elements = wrapper.querySelectorAll('.js-gsap-delay')
                    this.initDelayedAnimation(wrapper, elements)
                })

                // alt 2: delayed on mobile, single from tablet up
                this.delayedWrapOnMobile.forEach(wrapper => {
                    const elements = wrapper.querySelectorAll('.js-gsap-delay')
                    elements.forEach(element =>
                        this.initSingleAnimation(element)
                    )
                })
            },
            '(max-width: 639px)': () => {
                // alt 1: single on mobile, delayed from tablet up
                this.delayedWrapFromTablet.forEach(wrapper => {
                    const elements = wrapper.querySelectorAll('.js-gsap-delay')
                    elements.forEach(element =>
                        this.initSingleAnimation(element)
                    )
                })

                // alt 2: delayed on mobile, single from tablet up
                this.delayedWrapOnMobile.forEach(wrapper => {
                    const elements = wrapper.querySelectorAll('.js-gsap-delay')
                    this.initDelayedAnimation(wrapper, elements)
                })
            },
            '(max-width: 1024px)': () => {
                if (this.shareLinks.length)
                    this.initSingleAnimation(this.shareLinks[0])

                // single on mobile and tablet, delayed from desktop up
                this.delayedWrapFromDesktop.forEach(wrapper => {
                    const elements = wrapper.querySelectorAll('.js-gsap-delay')
                    elements.forEach(element =>
                        this.initSingleAnimation(element)
                    )
                })
            },
            '(min-width: 1025px)': () => {
                if (this.shareLinks.length)
                    this.initShareVisibility(this.shareLinks[0])
                if (this.shareLinksWrap.length)
                    this.initShareSticking(this.shareLinksWrap[0])

                // single on mobile and tablet, delayed from desktop up
                this.delayedWrapFromDesktop.forEach(wrapper => {
                    const elements = wrapper.querySelectorAll('.js-gsap-delay')
                    this.initDelayedAnimation(wrapper, elements)
                })
            },
        })
    },
    initShareSticking: function (element) {
        ScrollTrigger.create({
            trigger: element.parentElement,
            endTrigger: '.js-gsap-share-pin-end',
            start: 'top 120px',
            end: 'bottom 408px',
            invalidateOnRefresh: true,
            pin: true,
        })
    },
    initShareVisibility: function (element) {
        gsap.fromTo(
            element,
            {
                x: -CONTENT_Y_DISTANCE,
                opacity: 0,
            },
            {
                scrollTrigger: {
                    trigger: element.parentElement,
                    start: 'bottom 100%',
                    end: 'bottom 80px',
                    invalidateOnRefresh: true,
                    toggleActions: 'play complete none reverse',
                },
                x: 0,
                opacity: 1,
            }
        )
    },
    initSingleAnimation: function (element, explicitTrigger = false) {
        const elementTrigger = explicitTrigger
            ? explicitTrigger
            : element.parentElement

        gsap.set(element, { y: CONTENT_Y_DISTANCE, opacity: 0 })

        ScrollTrigger.create({
            trigger: elementTrigger,
            start: 'top bottom-=90px',
            end: 'bottom 90px',
            invalidateOnRefresh: true,
            id: 'singleAnimation',
            onEnter: ({ direction, isActive }) =>
                gsap.to(element, constructAnimationVars(direction, isActive)),
            onLeave: ({ direction, isActive }) =>
                gsap.to(element, constructAnimationVars(direction, isActive)),
            onRefresh: ({ progress }) => {
                if (progress >= 1) gsap.set(element, { y: -CONTENT_Y_DISTANCE })
            },
            onToggle: ({ direction, isActive }) =>
                gsap.to(element, constructAnimationVars(direction, isActive)),
        })
        ScrollTrigger.create({
            trigger: elementTrigger,
            start: 'top bottom-=90px',
            end: 'bottom 90px',
            invalidateOnRefresh: true,
            onEnterBack: ({ direction, isActive }) =>
                gsap.to(element, constructAnimationVars(direction, isActive)),
            onLeaveBack: ({ direction, isActive }) =>
                gsap.to(element, constructAnimationVars(direction, isActive)),
        })

        $(element)
            .off('update:height')
            .on('update:height', () => ScrollTrigger.refresh())
    },
    initHeadingAnimation: function (element, explicitTrigger = false) {
        const elementTrigger = explicitTrigger
            ? explicitTrigger
            : element.parentElement

        gsap.set(element, { y: CONTENT_Y_DISTANCE, opacity: 0 })

        ScrollTrigger.create({
            trigger: elementTrigger,
            start: 'top bottom-=40px',
            end: 'bottom 90px',
            invalidateOnRefresh: true,
            onEnter: ({ direction, isActive }) =>
                gsap.to(element, constructAnimationVars(direction, isActive)),
            onLeave: ({ direction, isActive }) =>
                gsap.to(element, constructAnimationVars(direction, isActive)),
            onRefresh: ({ progress }) => {
                if (progress >= 1) gsap.set(element, { y: -CONTENT_Y_DISTANCE })
            },
            onToggle: ({ direction, isActive }) =>
                gsap.to(element, constructAnimationVars(direction, isActive)),
        })
    },
    initDelayedAnimation: function (wrapper, elements) {
        gsap.set(elements, { y: CONTENT_Y_DISTANCE, opacity: 0 })

        ScrollTrigger.create({
            trigger: wrapper,
            start: 'top bottom-=90px',
            end: 'bottom 100px',
            invalidateOnRefresh: true,
            onEnter: ({ direction, isActive }) =>
                gsap.to(
                    elements,
                    constructAnimationVars(direction, isActive, true)
                ),
            onLeave: ({ direction, isActive }) =>
                gsap.to(
                    elements,
                    constructAnimationVars(direction, isActive, true)
                ),
            onRefresh: ({ progress }) => {
                if (progress >= 1)
                    gsap.set(elements, { y: -CONTENT_Y_DISTANCE })
            },
            onToggle: ({ direction, isActive }) =>
                gsap.to(
                    elements,
                    constructAnimationVars(direction, isActive, true)
                ),
        })

        ScrollTrigger.create({
            trigger: wrapper,
            start: 'top bottom-=100px',
            end: 'bottom 120px',
            invalidateOnRefresh: true,
            onEnterBack: ({ direction, isActive }) =>
                gsap.to(
                    elements,
                    constructAnimationVars(direction, isActive, true)
                ),
            onLeaveBack: ({ direction, isActive }) =>
                gsap.to(
                    elements,
                    constructAnimationVars(direction, isActive, true)
                ),
            onRefresh: ({ progress }) => {
                if (progress >= 1)
                    gsap.set(elements, { y: -CONTENT_Y_DISTANCE })
            },
            onToggle: ({ direction, isActive }) =>
                gsap.to(
                    elements,
                    constructAnimationVars(direction, isActive, true)
                ),
        })
    },
}

// Animate petals
const initPetals = () => {
    gsap.utils.toArray('.website-section').forEach(section => {
        const petals = section.querySelectorAll('.js-petal-inner')

        petals.forEach(petal => {
            const parent = petal.parentElement
            const size = petal.dataset.size
            const petalScrub = size * 0.1 + 0.5 // from 0.6 (size 1) to 1.7 (size 16)
            const petalY = 310 - size * 10 // from 300 (size 1) to 150 (size 16)

            if (petal.dataset.stick) {
                // Snap in place
                gsap.from(petal, {
                    scrollTrigger: {
                        trigger: parent,
                        toggleActions: 'play pause resume reverse',
                        start: 'top 100%',
                        end: 'bottom 36%',
                        scrub: petalScrub,
                    },
                    // duration: 1.5,
                    y: petalY * 0.7,
                    ease: 'none',
                })
            } else {
                // Continue uninterrupted
                gsap.fromTo(
                    petal,
                    {
                        y: petalY * 0.7,
                        ease: 'none',
                    },
                    {
                        y: -petalY * 0.7,
                        ease: 'none',
                        scrollTrigger: {
                            trigger: parent,
                            scrub: petalScrub,
                            start: 'top 100%',
                            end: 'bottom 0%',
                        },
                    }
                )
            }
        })
    })
}

// Who we are section - butterfly animation
gsap.utils.toArray('.js-gsap-butterfly-wing').forEach((wing, index) => {
    const isOnRightSide = index === 1 || index === 3
    const triggerElement = document.querySelector('.js-gsap-butterfly-shape')
    const wingShadow = wing.querySelector('.js-gsap-butterfly-shadow')
    const wingIcon = wing.querySelector('.js-gsap-butterfly-icon')
    const wingText = wing.querySelector('.js-gsap-butterfly-text')

    const butterfly = gsap
        .timeline({
            paused: true,
        })
        .from(wingIcon, {
            scale: 0,
            opacity: 0,
            duration: 0.7,
            ease: 'easeOutCubic',
        })
        .from(
            wingShadow,
            {
                scaleX: 0,
                opacity: 0,
                duration: 0.5,
                ease: 'easeOutCubic',
            },
            '-=0.3'
        )
        .from(
            wingText,
            {
                x: isOnRightSide ? -30 : 30,
                opacity: 0,
                duration: 0.8,
                ease: 'easeOutCubic',
            },
            '-=0.2'
        )

    // When Scrolling down - menu hidden
    ScrollTrigger.create({
        trigger: triggerElement,
        start: '50% bottom',
        end: '50% top',
        onEnter: () => butterfly.play(),
        onLeave: () => butterfly.reverse(),
    })

    // When Scrolling back up - menu visible
    ScrollTrigger.create({
        trigger: triggerElement,
        start: '50% bottom',
        end: '50% 80px',
        onEnterBack: () => butterfly.play(),
        onLeaveBack: () => butterfly.reverse(),
    })
})

$(document).ready(function () {
    contentAnimation.init()

    initPetals()
    $(document).on('update:petalsCount', () => {
        gsap.killTweensOf('.js-petal-inner')
        initPetals()
    })
})

$(window).on('resize', () => {
    setTimeout(() => ScrollTrigger.refresh(), 400)
})
